import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ContentChild, EventEmitter, HostListener,
  Input, OnDestroy,
  OnInit, Output,
  TemplateRef
} from "@angular/core";
import {IntDropdownItemDirective} from "./int-dropdown-item.directive";
import {BehaviorSubject, combineLatest, zip} from "rxjs";
import {IIntSelectPayload} from "../../../types";
import {distinctUntilChanged, takeUntil} from "rxjs/operators";
import {DestroyStreamService} from "../../../services";
import {KEY_CODES, KEYBOARD_ACTIONS} from "../../../enums";

@Component({
  selector: 'int-dropdown-list',
  templateUrl: './int-dropdown-list.component.html',
  styleUrls: ['./int-dropdown-list.component.scss'],
  providers: [
    DestroyStreamService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IntDropdownListComponent implements AfterViewInit, OnInit, OnDestroy {

  template$ = new BehaviorSubject<TemplateRef<any> | null>(null);

  list$ = new BehaviorSubject<Array<IIntSelectPayload> | null>([]);

  selected$ = new BehaviorSubject<Array<string> | null>([]);

  isVisible = false;

  activeID$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  @Input() tabindex = 1;

  @Input()
  set list(data: Array<IIntSelectPayload> | null) {
    this.list$.next(data || []);
  }

  @Input()
  set selectedValues(data: Array<string> | null) {
    this.selected$.next(data || []);
  }

  @Output() onClick = new EventEmitter<IIntSelectPayload>()

  @ContentChild(IntDropdownItemDirective, {read: TemplateRef}) readonly templateItem!: TemplateRef<any>;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly destroy$: DestroyStreamService
  ) {

    combineLatest([this.selected$, this.list$])
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(([selected, list]) => {

        const firstSelectedRecord = (list || [])
          .map((record, id) => {
            return {
              ...record,
              id
            }
          }).find((record) => {
            return (selected || []).includes(record.value)
          });

        const id = firstSelectedRecord ? firstSelectedRecord.id : 0;
        this.activeID$.next(id);

      })
  }

  @HostListener('window:keydown', ['$event'])
  keypress(e: KeyboardEvent) {

    if(!this.isVisible) {
      return;
    }

    const keyCode = e.keyCode;
    if(keyCode === KEYBOARD_ACTIONS.PREVIOUS) {
      this.navigate(-1);
    }
    if(keyCode === KEYBOARD_ACTIONS.NEXT) {
      this.navigate(1);
    }
    if(keyCode === KEYBOARD_ACTIONS.ENTER) {
      e.stopPropagation();
      e.preventDefault();
      this.onKeyPressedSelect();
    }
  }

  ngOnInit() {
    this.isVisible = true;
  }

  ngOnDestroy() {
    this.isVisible = false;
  }

  ngAfterViewInit() {
    this.template$.next(this.templateItem);
    this.cdr.detectChanges();
  }

  navigate(direction: number) {
    if(this.activeID$.value === null) {
      return;
    }
    const _i = (this.activeID$.value + direction);
    const currentActiveID =
      _i > this.listLength - 1
        ? 0
        : _i < 0
          ? this.listLength - 1
          : _i;
    this.activeID$.next(currentActiveID);
  }

  onKeyPressedSelect() {
    const payload = (this.list$.value || []).find((_, index) => {
      return index === this.activeID$.value;
    });
    if(payload) {
      this.onSelectItem(payload, this.activeID$.value || 0);
    }
  }

  get templateRef(): TemplateRef<any> | null {
    return this.template$.value;
  }

  get isTemplateExist() {
    return Boolean(this.templateRef);
  }

  get listLength() {
    return (this.list$.value || []).length;
  }

  isSelected(value: string): boolean {
    return (this.selected$.value || []).includes(value);
  }

  onSelectItem(item: IIntSelectPayload, index: number) {
    this.onClick.emit(item);
    this.activeID$.next(index);
  }
}
